/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import './budget-strip-2023.module.css';

const BudgetStrip2023 = ({ sponsors, config, impressionConfig, widget = false }) => {
  const { budget = {}, plurals = {} } = config.features || {};
  const { budget_date, budgetstrip_count_strip, top_banner_style } = budget;
  const { activateAssociate, activateBanking, activePresents, activePayment, budgetPageLink } =
    plurals;

  const calculateTimeLeft = () => {
    const difference = new Date(budget_date) - new Date();
    if (difference <= 0) return null;

    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / (1000 * 60)) % 60),
      seconds: Math.floor((difference / 1000) % 60),
    };
  };

  const [timeLeft, setTimeLeft] = useState(() => calculateTimeLeft());

  useEffect(() => {
    if (!budget_date) return;

    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft();
      setTimeLeft(newTimeLeft);
    }, 1000);

    return () => clearInterval(timer);
  }, [budget_date]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const dm = urlParams.get('dm');
    const disabledm = urlParams.get('disabledm');

    let darkModeEnabled = false;

    if (dm === '1') {
      // Force dark mode ON
      darkModeEnabled = true;
    } else if (disabledm === '1') {
      // Force dark mode OFF
      darkModeEnabled = false;
    } else {
      // Use system preference
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      darkModeEnabled = mediaQuery.matches;
    }
    const view = urlParams.get('view');
    if (widget && view === 'apps') {
      // Set background color
      const body = document.getElementsByTagName('body')[0];
      body.style.backgroundColor = darkModeEnabled ? '#242424' : '#FFFFFF';
    }
  }, []);

  useEffect(() => {
    if (!impressionConfig?.enable) return;
    const urlParams = new URLSearchParams(window.location.search);
    const view = urlParams.get('view');
    const width = window.innerWidth;
    const isMobile = width <= 767;

    const adUnit =
      widget && view === 'apps'
        ? impressionConfig?.platforms?.widget
        : isMobile
          ? impressionConfig?.platforms?.mobile
          : impressionConfig?.platforms?.desktop;

    if (!adUnit) return;

    let passbackDiv = document.getElementById('gpt-passback');
    if (!passbackDiv) {
      passbackDiv = document.createElement('div');
      passbackDiv.id = 'gpt-passback';
      passbackDiv.className = 'passbackAd';
      document.body.appendChild(passbackDiv);
    }

    window.googletag = window.googletag || { cmd: [] };

    const loadGPT = () => {
      window.googletag.cmd.push(() => {
        const slots = window.googletag.pubads().getSlots();
        slots.forEach((slot) => window.googletag.destroySlots([slot]));

        window.googletag
          .defineSlot(adUnit, [1, 1], 'gpt-passback')
          .addService(window.googletag.pubads());

        if (!window.__gptServicesEnabled) {
          window.googletag.enableServices();
          window.__gptServicesEnabled = true;
        }

        window.googletag.display('gpt-passback');
      });
    };

    if (!document.querySelector('script[src*="gpt.js"]')) {
      const gptScript = document.createElement('script');
      gptScript.src = impressionConfig.scriptUrl;
      gptScript.async = true;
      gptScript.crossOrigin = 'anonymous';
      gptScript.onload = loadGPT;
      document.head.appendChild(gptScript);
    } else {
      loadGPT();
    }

    return () => {
      const el = document.getElementById('gpt-passback');
      if (el) el.remove();
    };
  }, [impressionConfig?.enable, widget]);

  const bankingPartners = sponsors.filter(
    ({ sponsorType, enable }) => sponsorType === 'banking' && enable,
  );

  const associateSponsors = sponsors.filter(
    ({ sponsorType, enable }) => sponsorType === 'associate' && enable,
  );

  const paymentSponsors = sponsors.filter(
    ({ sponsorType, enable }) => sponsorType === 'payment' && enable,
  );

  const presentsSponsors = sponsors.find(
    ({ sponsorType, enable }) => sponsorType === 'presents' && enable,
  );

  const renderTimer = () => {
    if (!budget_date || !budgetstrip_count_strip) return null;

    if (!timeLeft) return null;

    const { days, hours, minutes, seconds } = timeLeft;

    if (days === 0 && hours === 0 && minutes === 0 && seconds === 0) {
      return null;
    }

    let timeText = '';
    let mobileTimeText = '';

    if (days > 0) {
      timeText = `${days} Days : ${hours} Hours : ${minutes} Mins : ${seconds} Secs`;
      mobileTimeText = `${days} Days : ${hours} Hrs : ${minutes} Mins : ${seconds} Secs`;
    } else if (hours > 0) {
      timeText = `${hours} Hours : ${minutes} Mins : ${seconds} Secs`;
      mobileTimeText = `${hours} Hrs : ${minutes} Mins : ${seconds} Secs`;
    } else if (minutes > 0) {
      timeText = `${minutes} Mins : ${seconds} Secs`;
      mobileTimeText = `${minutes} Mins : ${seconds} Secs`;
    } else {
      timeText = `${seconds} Secs`;
      mobileTimeText = `${seconds} Secs`;
    }

    return (
      <div styleName="budget-countdown-strip">
        <div styleName="heading">
          <div className="desktop-only">
            <span styleName="starts-in">Starts in</span> {timeText}
          </div>
          <div className="mobile-only">
            <span styleName="starts-in">Starts in</span> {mobileTimeText}
          </div>
        </div>
      </div>
    );
  };

  const renderSponsorGrid = (title, sponsorsList, class1, class2) => {
    if (!sponsorsList || sponsorsList.length === 0) return null;

    return (
      <div styleName="sponsor-grid-wrapper" style={class1}>
        <div styleName="sponsor-text">
          <span>{title}</span>
        </div>
        <div styleName="sponsor-grid" style={class2}>
          {sponsorsList.map(({ url, width, height, altText, href, deskWidth, deskHeight }, i) => (
            <React.Fragment key={i}>
              <a
                href={href || '#'}
                target="_blank"
                rel="noreferrer noopener"
                className="desktop-only"
              >
                <img src={url} alt={altText} style={{ height: deskHeight, width: deskWidth }} />
              </a>
              <a
                href={href || '#'}
                target="_blank"
                rel="noreferrer noopener"
                className="mobile-only"
              >
                <img src={url} alt={altText} style={{ height: height, width: width }} />
              </a>
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  const hasBanking = activateBanking && bankingPartners.length > 0;
  const hasAssociate = activateAssociate && associateSponsors.length > 0;
  const hasPayment = activePayment && paymentSponsors.length > 0;
  const presentsAvailable = activePresents && presentsSponsors?.enable;
  const hasAnySponsors = hasBanking || hasAssociate || hasPayment || presentsAvailable;

  const renderSponsors = () => {
    if (!hasBanking && !hasAssociate && !hasPayment) return null;

    return (
      <div
        styleName="container-with-text"
        style={{ ...top_banner_style?.styleSheet?.sponsorCards }}
      >
        {hasBanking &&
          renderSponsorGrid(
            'Co-powered by',
            bankingPartners,
            top_banner_style?.styleSheet?.coBanking,
            top_banner_style?.styleSheet?.coBankingCard,
          )}
        {hasAssociate &&
          renderSponsorGrid(
            'Associate Sponsors',
            associateSponsors,
            top_banner_style?.styleSheet?.coAssociate,
            top_banner_style?.styleSheet?.coAssociateCard,
          )}
        {hasPayment &&
          renderSponsorGrid(
            'Payment Partner',
            paymentSponsors,
            top_banner_style?.styleSheet?.coPayment,
            top_banner_style?.styleSheet?.coPaymentCard,
          )}
      </div>
    );
  };

  const getBannerStyle = () => {
    if (!top_banner_style) return {};
    if (hasAnySponsors) {
      return top_banner_style.non_sponsor_banner || {};
    } else {
      return top_banner_style.mobile_banner || {};
    }
  };

  const mobileBanner = getBannerStyle();

  const handleBannerClick = (e) => {
    // Only navigate if clicking on the banner background, not on nested links
    if (e.target.tagName !== 'A' && !e.target.closest('a[href]:not([href="/budget"])')) {
      window.open(budgetPageLink || '/budget', '_blank');
    }
  };

  const isSponsor = !hasBanking && !hasAssociate && !hasPayment;

  return (
    <div
      className={widget ? '' : 'container'}
      styleName={widget ? 'budget-strip-2026 widget-wrap' : 'budget-strip-2026'}
    >
      <div
        styleName="budget-strip-wrapper"
        style={{ ...top_banner_style?.styleSheet?.mainContainer }}
      >
        {/* Desktop Banner */}
        <div
          styleName={
            !timeLeft || !budgetstrip_count_strip ? 'budget-strip banner-radius' : 'budget-strip'
          }
          className="desktop-only"
          onClick={handleBannerClick}
          style={{
            background: `url(${top_banner_style?.desktop_banner?.url}) no-repeat center center/cover`,
            height: top_banner_style?.desktop_banner?.height,
            width: top_banner_style?.desktop_banner?.width || '100%',
            cursor: 'pointer',
            ...top_banner_style?.styleSheet?.deskStrip,
          }}
        >
          <div
            styleName="budget-logo-wrapper"
            style={{
              width: isSponsor ? '100%' : 'auto',
              ...top_banner_style?.styleSheet?.budgetContainer,
            }}
          >
            {presentsAvailable && (
              <a
                href={presentsSponsors?.href || '#'}
                target="_blank"
                rel="noreferrer noopener"
                styleName="presents-logo-link"
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={presentsSponsors?.url || ''}
                  alt={presentsSponsors?.altText}
                  style={{
                    height: presentsSponsors?.deskHeight,
                    width: presentsSponsors?.deskWidth,
                    ...top_banner_style?.styleSheet?.presentsLogo,
                  }}
                />
              </a>
            )}
            <img
              src={top_banner_style?.budgetBanner?.url || ''}
              alt="Budget 2026"
              style={{
                height: top_banner_style?.budgetBanner?.height,
                width: top_banner_style?.budgetBanner?.width,
              }}
            />
          </div>
          {renderSponsors()}
        </div>

        {/* Mobile Banner */}
        <div
          styleName={
            !timeLeft || !budgetstrip_count_strip ? 'budget-strip banner-radius' : 'budget-strip'
          }
          className="mobile-only"
          onClick={handleBannerClick}
          style={{
            background: `url(${mobileBanner?.url}) no-repeat center center/cover`,
            height: mobileBanner?.height,
            width: mobileBanner?.width || '100%',
            cursor: 'pointer',
            ...mobileBanner?.styleSheet?.mbStrip,
          }}
        >
          <div
            styleName="budget-logo-wrapper"
            style={{
              alignItems: isSponsor ? 'center' : 'flex-start',
              ...top_banner_style?.styleSheet?.budgetContainer,
            }}
          >
            {presentsAvailable && (
              <a
                href={presentsSponsors?.href || '#'}
                target="_blank"
                rel="noreferrer noopener"
                styleName="presents-logo-link"
                style={{ ...top_banner_style?.styleSheet?.presentsLogo }}
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={presentsSponsors?.url || ''}
                  alt={presentsSponsors?.altText}
                  style={{
                    height: presentsSponsors?.height,
                    width: presentsSponsors?.width,
                    ...top_banner_style?.styleSheet?.presentsLogo,
                  }}
                />
              </a>
            )}
            <img
              src={top_banner_style?.budgetBanner?.url || ''}
              alt="Budget 2026"
              style={{
                height: top_banner_style?.budgetBanner?.height,
                width: top_banner_style?.budgetBanner?.width,
              }}
            />
          </div>
          {renderSponsors()}
        </div>

        {/* Timer */}
        {renderTimer()}
      </div>
    </div>
  );
};

BudgetStrip2023.propTypes = {
  config: PropTypes.shape({
    features: PropTypes.shape({
      budget: PropTypes.shape({
        budget_date: PropTypes.string,
        budgetstrip_count_strip: PropTypes.bool,
        top_banner_style: PropTypes.shape({
          desktop_banner: PropTypes.object,
          mobile_banner: PropTypes.object,
          non_sponsor_banner: PropTypes.object,
          desk_container: PropTypes.object,
          budgetBanner: PropTypes.object,
          styleSheet: PropTypes.object,
        }),
      }),
      plurals: PropTypes.shape({
        activateAssociate: PropTypes.bool,
        activePayment: PropTypes.bool,
        activateBanking: PropTypes.bool,
        budgetPageLink: PropTypes.string,
        activePresents: PropTypes.bool,
      }),
    }),
  }).isRequired,
  sponsors: PropTypes.arrayOf(
    PropTypes.shape({
      sponsorType: PropTypes.string,
      enable: PropTypes.bool,
      url: PropTypes.string,
      width: PropTypes.number,
      height: PropTypes.number,
      altText: PropTypes.string,
      href: PropTypes.string,
      deskWidth: PropTypes.number,
      deskHeight: PropTypes.number,
    }),
  ),
  impressionConfig: PropTypes.shape({
    enable: PropTypes.bool,
    scriptUrl: PropTypes.string,
    platforms: PropTypes.shape({
      desktop: PropTypes.string,
      mobile: PropTypes.string,
      widget: PropTypes.string,
    }),
  }),
  widget: PropTypes.bool,
};

BudgetStrip2023.defaultProps = {
  sponsors: [],
  impressionConfig: {},
  widget: false,
};

const mapStateToProps = (state) => {
  const { features = {}, publisher = {} } = state.qt.config || {};
  return {
    config: { features, publisher },
    sponsors: get(state, ['qt', 'config', 'publisher', 'budget_2026_sponsors'], []),
    impressionConfig: get(state, ['qt', 'config', 'publisher', 'sponsor-impression-tag'], {}),
  };
};

export default connect(mapStateToProps)(BudgetStrip2023);
